import { NgForOf, NgIf } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Router, Scroll } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ECollection } from '../../enums/ECollection';
import { EProductTypes } from '../../enums/EProductTypes';
import { IQueryFormat } from '../../enums/types';
import { ICategories, ICategories_P, ICategories_S } from '../../interfaces/ICategories';
import { IMainCategories, IMainCategories_P, IMainCategories_S } from '../../interfaces/IMainCategories';
import { IProducts, IProducts_P, IProducts_S, IProImagesColorArr } from '../../interfaces/IProducts';
import { ISubCategories_P, ISubCategories_S } from '../../interfaces/ISubCategories';
import { ISwCoverImgHome, ISwCoverImgHome_P, ISwCoverImgHome_S } from '../../interfaces/ISwCoverImgHome';
import { CategoriesService } from '../../service/categories.service';
import { CoverImgHomeService } from '../../service/cover-img-home.service';
import { MainCategoriesService } from '../../service/main-categories.service';
import { ProductsService } from '../../service/products.service';
import { SubCategoriesService } from '../../service/sub-categories.service';
import { SelectedBrandModelService } from '../../service/selected-brand-model.service';
import * as _ from 'lodash';
import { AutoSearch } from '../auto-search/auto-search';

declare const $: any;

@Component({
    selector: 'app-home',
    standalone: true,
    templateUrl: './home.html',
    imports: [FormsModule, Button, Tooltip, NgForOf, NgIf, AutoSearch],
    styleUrl: 'home.scss',
})
export class Home implements OnInit, AfterViewInit {
    loading: boolean = false;
    item: any;
    activeMenu: string = '';
    coverImagesArr: ISwCoverImgHome[] = [];
    mainCategories: IMainCategories[] = [];
    fileUrl = environment.FILE_URL;
    bestSellerProducts: IProducts[] = [];
    productsBundle: IProducts[] = [];
    EProductTypes = EProductTypes;
    categoryCarousal: any[] = [];
    phoneCategoryId: string | undefined = '';
    cityCollectionCategory: ICategories | undefined;

    showLeftArrow: boolean[] = [];
    showRightArrow: boolean[] = [];
    @ViewChildren('categoryScroll') categoryScroll!: QueryList<ElementRef>;

    // Split out because a category may carry a carousel banner, a card image, or both;
    // rendering the unfiltered list leaves empty grid columns where the image is absent.
    categoryBanners: any[] = [];
    categoryCards: any[] = [];

    constructor(
        private cd: ChangeDetectorRef,
        private coverImgHomeService: CoverImgHomeService,
        private mainCategoriesService: MainCategoriesService,
        private subCategoriesService: SubCategoriesService,
        private categoriesService: CategoriesService,
        private productsService: ProductsService,
        private router: Router,
        private selectedBrandModelService: SelectedBrandModelService
    ) {
    }

    // Angular's own withInMemoryScrolling restoration fires ~1 tick after navigation, while the
    // page is still short (data loads async below), so it lands short of the real position.
    // Capture the position it broadcasts and re-apply it once the content has actually grown.
    // Note: getCurrentNavigation()?.trigger is unreliable here (observed 'undefined' on real
    // browser back-navigations), so restoration is keyed off receiving a real Scroll position
    // instead — RouterScroller only ever sends a non-null position for popstate navigations.
    private restoredScrollY: number | null = null;

    async ngOnInit() {
        this.router.events
            .pipe(filter((e): e is Scroll => e instanceof Scroll))
            .subscribe(e => {
                if (e.position) this.restoredScrollY = e.position[1];
            });

        this.loading = true;
        setTimeout(async () => {
            try {
                await this.loadImageCover();
                await this.loadMainCategory();
                await this.loadCategoryCarousal();
                await this.loadProducts();
                this.cd.detectChanges();
                // Initialise carousels only after data is in the DOM
                setTimeout(() => {
                    this.bestSellerSection();
                    this.productBundleSection();
                    this.productCaseBannerSection();
                    this.cd.detectChanges();

                    if (this.restoredScrollY !== null) {
                        window.scrollTo({ top: this.restoredScrollY, behavior: 'auto' });
                    } else {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                }, 100);
            } catch (err) {
                console.error(err);
            } finally {
                this.loading = false;
                this.cd.detectChanges();
            }
        }, 500);
    }

    scrollCategory(index: number, direction: number) {
        const el = this.categoryScroll.toArray()[index].nativeElement;
        el.scrollBy({ left: direction * 240, behavior: 'smooth' });
        setTimeout(() => this.updateArrowVisibility(el, index), 300);
    }

    onScroll(index: number) {
        const el = this.categoryScroll.toArray()[index].nativeElement;
        this.updateArrowVisibility(el, index);
    }

    recalculateArrows(index: number) {
        const el = this.categoryScroll.toArray()[index].nativeElement;
        this.updateArrowVisibility(el, index);
    }

    private updateArrowVisibility(el: HTMLElement, index: number) {
        const maxScroll = el.scrollWidth - el.clientWidth;
        this.showLeftArrow[index] = el.scrollLeft > 5;
        this.showRightArrow[index] = maxScroll > 5 && el.scrollLeft < maxScroll - 5;
    }

    // Category tiles used to always send the shopper through /selection to pick brand/model again.
    // If they already have a stored preference, skip straight to the catalog with it applied.
    goToCatalog(mainCategoryId?: string, subCategoryId?: string) {
        const stored = this.selectedBrandModelService.get();
        if (stored) {
            this.router.navigate(['/phone-cases'], {
                queryParams: {
                    mainCategoryId,
                    subCategoryId,
                    brandId: stored.brandId,
                    modelId: stored.modelId,
                    modelName: stored.modelName
                }
            });
        } else {
            this.router.navigate(['/selection'], { queryParams: { mainCategoryId, subCategoryId } });
        }
    }

    openPhoneCases(menu: string, mainCategoryId?: string, subCategoryId?: string, categoryName?: string) {
        this.router.navigate(['/phone-cases'], {
            queryParams: {
                mainCategoryId,
                subCategoryId,
                categoryName
            },
        });
        this.activeMenu = menu;
    }

    // "Shop City Cases" browses a curated category across every model, so unlike other catalog
    // entry points it skips the brand/model prompt (modelSelectionGuard) instead of detouring
    // the shopper through /selection first.
    openCityCollection(menu: string) {
        this.router.navigate(['/phone-cases'], {
            queryParams: {
                mainCategoryId: this.cityCollectionCategory?.cat_main_category_id || this.phoneCategoryId,
                subCategoryId: this.cityCollectionCategory?.cat_sub_category_id,
                categoryName: this.cityCollectionCategory?.cat_name_str,
                skipModelSelection: '1'
            },
        });
        this.activeMenu = menu;
    }

    openFaqs(menu: string) {
        this.router.navigate(['/faqs']);
        this.activeMenu = menu;
    }

    openShippingAndReturn(menu: string) {
        this.router.navigate(['/shipping-and-return']);
        this.activeMenu = menu;
    }

    openOurStory(menu: string) {
        this.router.navigate(['/our-story']);
        this.activeMenu = menu;
    }

    async loadImageCover() {
        // this.loading = true;
        this.coverImagesArr = [];
        let query = <IQueryFormat>{
            find: <ISwCoverImgHome_P>{
                active: true,
            },
            select: <ISwCoverImgHome_S>{
                order: 1,
                cih_url__arr: 1,
                cih_name_str: 1,
            },
            sort: <ISwCoverImgHome_S>{ order: 1 },
        };
        let resp = await this.coverImgHomeService.query(query);
        if (resp && resp.length)
            for (let item of resp) {
                const uploadPath = item.cih_url__arr?.[0]?.uploadPath;
                if (uploadPath) item.fileUrl = `${this.fileUrl}${uploadPath}`;
            }
        this.coverImagesArr = resp;
        // this.loading = false;
        this.cd.detectChanges();
    }

    async loadMainCategory() {
        // this.loading = true;
        this.mainCategories = [];
        let query = <IQueryFormat>{
            find: <IMainCategories_P>{
                active: true,
            },
            select: <IMainCategories_S>{
                smc_img_url__arr: 1,
                smc_name_str: 1,
                smc_order_num: 1,
            },
            sort: <IMainCategories_S>{ smc_order_num: 1 },
        };
        let resp = await this.mainCategoriesService.query(query);
        if (resp && resp.length) {
            let subCategories = await this.loadSubCategory(_.map(resp, '_id'));
            let subCategoriesMap = _.groupBy(
                subCategories,
                'ssc_main_category_id'
            );
            for (let item of resp) {
                if (subCategoriesMap[item._id])
                    item.subCategories = subCategoriesMap[item._id];
                const uploadPath = item.smc_img_url__arr?.[0]?.uploadPath;
                if (uploadPath) item.fileUrl = `${this.fileUrl}${uploadPath}`;
            }
        }
        this.mainCategories = resp;

        // storing the phone cases main category id
        // this.phoneCategoryId = this.mainCategories.find((item) => item.smc_name_str == "Phone Cases")?._id;
        this.phoneCategoryId = this.mainCategories.find(item => new RegExp('Phone Cases', 'i').test(item.smc_name_str))?._id;
        // this.loading = false;
    }

    async loadSubCategory(mainCategoriesIds: string[]) {
        // this.loading = true;
        let query = <IQueryFormat>{
            find: <ISubCategories_P>{
                active: true,
                ssc_main_category_id: <any>{ $in: mainCategoriesIds },
            },
            select: <ISubCategories_S>{
                ssc_img_url__arr: 1,
                ssc_name_str: 1,
                ssc_order_num: 1,
                ssc_main_category_id: 1,
            },
            sort: <ISubCategories_S>{ ssc_order_num: 1 },
        };
        let res = await this.subCategoriesService.query(query);
        if (res && res.length)
            for (let item of res) {
                const uploadPath = item.ssc_img_url__arr?.[0]?.uploadPath;
                if (uploadPath) item.fileUrl = `${this.fileUrl}${uploadPath}`;
            }
        // this.loading = false;

        return res;
    }

    async loadCategoryCarousal() {
        this.categoryCarousal = [];
        this.categoryBanners = [];
        this.categoryCards = [];
        let query = <IQueryFormat>{
            find: <ICategories_P>{
                active: true,
            },
            select: <ICategories_S>{
                cat_name_str: 1,
                cat_main_category_id: 1,
                cat_sub_category_id: 1,
                cat_order_num: 1,
                cat_carousal_img_url__arr: 1,
                cat_card_img_url_arr: 1,
            },
            sort: <ICategories_S>{ cat_order_num: 1 },
        };
        let resp = await this.categoriesService.query(query);

        // 'City Collection' is a category (cat_name_str), not a sub-category — it carries no
        // carousel/card image, so it would otherwise be skipped by the image filter below.
        // Matched on "city" alone since admin has renamed this before.
        this.cityCollectionCategory = resp?.find((item: any) => /cit(y|ies)/i.test(item.cat_name_str));
        if (!this.cityCollectionCategory) console.warn('[home] no "City Collection" category matched; "Shop City Cases" will open the full catalog', resp?.map((i: any) => i.cat_name_str));

        if (resp && resp.length)
            for (let item of resp) {
                if (!item.cat_carousal_img_url__arr?.length && !item.cat_card_img_url_arr?.length) continue;

                const uploadPath = item.cat_carousal_img_url__arr?.[0]?.uploadPath;
                const cardUploadPath = item.cat_card_img_url_arr?.[0]?.uploadPath;

                this.categoryCarousal.push({
                    _id: item._id,
                    categoryName: item.cat_name_str,
                    imageUrl: uploadPath ? `${this.fileUrl}${uploadPath}` : undefined,
                    mainCategory: item.cat_main_category_id,
                    subCategory: item.cat_sub_category_id,
                    cardImageUrl: cardUploadPath ? `${this.fileUrl}${cardUploadPath}` : '',
                });
            }
        this.categoryBanners = this.categoryCarousal.filter(item => item.imageUrl);
        this.categoryCards = this.categoryCarousal.filter(item => item.cardImageUrl);
        this.cd.detectChanges();
    }

    ngAfterViewInit(): void {
        this.categoryScroll.forEach((ref, idx) => {
            this.updateArrowVisibility(ref.nativeElement, idx);
        });

        const pointer = document.getElementById('hoverPointer') as HTMLElement;
        const hoverTarget = document.querySelector(
            '.hero-carousel'
        ) as HTMLElement;
        const leftIcon = document.getElementById('hoverLeft') as HTMLElement;
        const rightIcon = document.getElementById('hoverRight') as HTMLElement;
        const carousel = document.getElementById(
            'carouselExampleFade'
        ) as HTMLElement;

        if (hoverTarget && pointer && leftIcon && rightIcon) {
            hoverTarget.addEventListener('mouseenter', () => {
                pointer.style.display = 'block';
            });

            hoverTarget.addEventListener('mousemove', (e: MouseEvent) => {
                pointer.style.top = `${e.clientY}px`;
                pointer.style.left = `${e.clientX}px`;

                const midPoint =
                    hoverTarget.offsetLeft + hoverTarget.offsetWidth / 2;

                if (e.clientX < midPoint) {
                    leftIcon.classList.remove('d-none');
                    rightIcon.classList.add('d-none');
                } else {
                    leftIcon.classList.add('d-none');
                    rightIcon.classList.remove('d-none');
                }
            });

            hoverTarget.addEventListener('mouseleave', () => {
                pointer.style.display = 'none';
            });

            // Clicking to change slides
            hoverTarget.addEventListener('click', (e: MouseEvent) => {
                const midPoint =
                    hoverTarget.offsetLeft + hoverTarget.offsetWidth / 2;

                if (e.clientX < midPoint) {
                    // Clicked left
                    const prevButton = carousel.querySelector(
                        '.carousel-control-prev'
                    ) as HTMLElement;
                    prevButton?.click();
                } else {
                    // Clicked right
                    const nextButton = carousel.querySelector(
                        '.carousel-control-next'
                    ) as HTMLElement;
                    nextButton?.click();
                }
            });
        }
    }

    bestSellerSection() {
        if (typeof $ !== 'undefined' && $('#owl-carousel').length) {
            $('#owl-carousel').owlCarousel({
                items: 1,
                loop: true,
                nav: false,
                margin: 0,
                autoplay: true,
                autoplayTimeout: 3000,
                autoplayHoverPause: true,
                dots: true,
                responsiveClass: true,
                responsive: {
                    0: {
                        items: 1.5,
                    },
                    600: {
                        items: 2,
                    },
                    1030: {
                        items: 3,
                    },
                    1130: {
                        items: 3,
                    },
                    1280: {
                        items: 3,
                    },
                    1400: {
                        items: 3,
                    },
                    1700: {
                        items: 4,
                    },
                    1920: {
                        items: 4,
                    },
                },
            });
        }
    }

    productBundleSection() {
        if (typeof $ !== 'undefined' && $('#owl-carousel1').length) {
            $('#owl-carousel1').owlCarousel({
                items: 1,
                loop: true,
                nav: false,
                margin: 0,
                autoplay: true,
                autoplayTimeout: 3000,
                autoplayHoverPause: true,
                dots: true,
                responsiveClass: true,
                responsive: {
                    0: {
                        items: 1.5,
                    },
                    600: {
                        items: 2,
                    },
                    1030: {
                        items: 2,
                    },
                    1130: {
                        items: 3,
                    },
                    1280: {
                        items: 3,
                    },
                    1400: {
                        items: 3,
                    },
                    1700: {
                        items: 4,
                    },
                    1920: {
                        items: 4,
                    },
                },
            });
        }
    }

    productCaseBannerSection() {
        if (typeof $ !== 'undefined' && $('#owl-carousel3').length) {
            $('#owl-carousel3').owlCarousel({
                items: 1,
                loop: true,
                margin: 0,
                autoplay: true,
                slideTransition: 'linear',
                autoplayTimeout: 3000,
                autoplaySpeed: 1000,
                dots: true,
                autoplayHoverPause: false,
                // The banner art is portrait (~0.83), so height is driven by slot
                // width: items are stepped up on wider screens to hold the card
                // near ~576px tall instead of filling the viewport.
                responsive: {
                    0: {
                        items: 1,
                    },
                    600: {
                        items: 2,
                    },
                    1030: {
                        items: 2,
                    },
                    1130: {
                        items: 2,
                    },
                    1280: {
                        items: 2,
                    },
                    1400: {
                        items: 3,
                    },
                    1700: {
                        items: 3,
                    },
                    1800: {
                        items: 4,
                    },
                },
            });
        }
    }

    async loadProducts() {
        // this.loading = true;
        this.bestSellerProducts = [];
        this.productsBundle = [];
        let query = <IQueryFormat>{
            find: <IProducts_P>{
                active: true,
                pro_product_type_str: <any>{ $in: [EProductTypes.BESTSELLER, EProductTypes.PRODUCT_BUNDLE] }
            },
            select: <IProducts_S>{
                pro_name_str: 1,
                pro_price_num: 1,
                pro_sale_num: 1,
                pro_discount_price_num: 1,
                // pro_images_color_arr: 1,
                pro_images_arr: 1,
                pro_category_id: 1,
                pro_product_type_str: 1
            },
            deep: [
                {
                    s_key: 'pro_category_id',
                    t_instance: environment.INS,
                    t_db: environment.DB,
                    t_col: ECollection.SW_CATEGORIES,
                    t_key: '_id',
                    select: <ICategories_S>{
                        cat_sub_category_id: 1,
                        cat_main_category_id: 1,
                        cat_name_str: 1
                    },
                },
            ],
            sort: <IProducts_S>{ pro_name_str: 1 },
        };
        let resp = await this.productsService.query(query);
        for (let item of resp) {
            item.colors = [];
            if (item.pro_images_arr && item.pro_images_arr.length) {
                const colorArr = item.pro_images_arr;

                const uploadPath = colorArr[0]?.uploadPath;
                if (uploadPath) item.fileUrl = `${this.fileUrl}${uploadPath}`;

                const uploadPath1 = colorArr[1]?.uploadPath;
                if (uploadPath1) item.fileUrl1 = `${this.fileUrl}${uploadPath1}`;

                // const uploadPath = colorArr?.[0]?.pro_template_arr?.[0]?.uploadPath;
                // if (uploadPath) item.fileUrl = `${this.fileUrl}${uploadPath}`;
                //
                // const uploadPath1 = colorArr?.[0]?.pro_template_arr?.[1]?.uploadPath;
                // if (uploadPath1) item.fileUrl1 = `${this.fileUrl}${uploadPath1}`;

                // for (let i of item.pro_images_color_arr) {
                //     if (i.color === EColor.Black) i.code = '#000000';
                //     if (i.color === EColor.White) i.code = '#f1f1f1';
                // }
            }
        }

        // console.log(this.categoryCarousal);
        this.bestSellerProducts = resp.filter((item: IProducts) => item.pro_product_type_str === EProductTypes.BESTSELLER);
        this.productsBundle = resp.filter((item: IProducts) => item.pro_product_type_str === EProductTypes.PRODUCT_BUNDLE);
        // this.bestSellerProducts = resp;
        // this.productsBundle = resp;
        // this.loading = false;
        this.cd.detectChanges();
    }

    clickColor(event: Event, product: IProducts, color: IProImagesColorArr, mode: string) {
        event.stopPropagation();
        if (mode === EProductTypes.BESTSELLER) {
            for (let pro of this.bestSellerProducts) {
                if (pro._id === product._id) {
                    const uploadPath = color.pro_template_arr?.[0]?.uploadPath;
                    pro.fileUrl = `${this.fileUrl}${uploadPath}`;
                    const uploadPath1 = color.pro_template_arr?.[1]?.uploadPath;
                    pro.fileUrl1 = `${this.fileUrl}${uploadPath1}`;
                }
            }
        }
        if (mode === EProductTypes.PRODUCT_BUNDLE) {
            for (let pro of this.productsBundle) {
                if (pro._id === product._id) {
                    const uploadPath = color.pro_template_arr?.[0]?.uploadPath;
                    pro.fileUrl = `${this.fileUrl}${uploadPath}`;
                    const uploadPath1 = color.pro_template_arr?.[1]?.uploadPath;
                    pro.fileUrl1 = `${this.fileUrl}${uploadPath1}`;
                }
            }
        }
    }

    openDetailsPage(event: Event, product: IProducts) {
        event.stopPropagation();
        this.router.navigate(['/phone-case-details'], {
            queryParams: {
                product: product._id,
            },
        });
    }
}
